/*
 * Cognito
 *
 * We use AWS Cognito to manage our users. https://aws.amazon.com/cognito/.
 *
 * Cognito checks a users credentials when they are signed in and return a valid JWT token.
 * We use the JWT token to issue requests to our APIs, where it is validated and checked to see permission levels for the user.
 *
 * Each user has a group that there are attached to. The group is what we use to determine permissions.
 * Each group has a set of permissions associated with it, and each user can also have another set of permissions.
 * Permissions are handled by LLIAM, our internal version of AWS IAM.
 * The LLIAM api allows us to get a LocusLabs policy object for the user. The policy object is what is used to determine
 * what totals permissions a user has from their group and individual policy objects.
 *
 * From a policy object, we can see if a user has read or write access to a venue, access to map notes, access to self publish, and more.
 *
 * This file is all about how we use Cognito to get a valid JWT token, and update user attributes.
 * Updating user attributes is relatively straightforward, we just need to call the associated AWS Amplify Auth call.
 * AWS Amplify Auth is an interface to Cognito.
 *
 * Whenever we want to get a valid JWT token for a user, we either refresh their current session if they are already logged in,
 * or get a new session by authenticating their credentials to get a valid JWT session.
 *
 * The cognito:groups attribute has the information we need to get the map for the user. The account id A1xxx#sub-group
 * determines what permissions and maps a user has access to, with the sub-group adding more permissions.
 * When we load a users attributes into redux we check the groups attribute for LocusLabs specific account ids, since
 * groups can also be SSO groups, which do not matter when attempting to fetch venues / mapnotes.
 */

import {
    signIn,
    confirmSignIn,
    signOut,
    getCurrentUser,
    fetchAuthSession,
    fetchUserAttributes,
    updatePassword,
    resetPassword,
    confirmResetPassword,
    updateUserAttributes,
    confirmUserAttribute,
} from 'aws-amplify/auth';
import { propOr, prop, filter, map, assoc, path } from 'ramda';
import * as Gtag from './gtag';

export const NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED';
export const NOT_AUTHORIZED_EXCEPTION = 'NotAuthorizedException';
export const INVALID_USER = 'UserNotFoundException';
export const UNKNOWN_AUTH_FAILURE = 'UNKNOWN_AUTH_FAILURE';
export const NEW_PASSWORDS_MUST_MATCH = 'NEW_PASSWORDS_MUST_MATCH';
export const CODE_MISMATCH_EXCEPTION = 'CodeMismatchException';
export const INVALID_PASSWORD_EXCEPTION = 'InvalidPasswordException';
export const LIMIT_EXCEEDED_EXCEPTION = 'LimitExceededException';
export const INVALID_PARAMETER_EXCEPTION = 'InvalidParameterException';

//new aws-amplify/auth v6 signInSteps
export const CONTINUE_SIGN_IN_WITH_MFA_SELECTION = 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION';
export const CONTINUE_SIGN_IN_WITH_TOTP_SETUP = 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP';
export const CONFIRM_SIGN_IN_WITH_SMS_CODE = 'CONFIRM_SIGN_IN_WITH_SMS_CODE';
export const CONFIRM_SIGN_IN_WITH_TOTP_CODE = 'CONFIRM_SIGN_IN_WITH_TOTP_CODE';
export const CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE = 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE';
export const CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';
export const CONFIRM_SIGN_UP = 'CONFIRM_SIGN_UP';
export const RESET_PASSWORD = 'RESET_PASSWORD';
export const DONE = 'DONE';

const authSuccessObject = user => ({ success: true, user });
const authFailureObject = error => ({
    success: false,
    code: propOr(UNKNOWN_AUTH_FAILURE, 'code', error),
    message: propOr('Unknown Error', 'message', error),
});

/*
 * Sign the user out of Cognito
 * Impure: calls Cognito
 * @sig signOutUser :: String -> ()
 */
export const signOutUser = async userId => {
    await signOut();
    Gtag.recordLogout(userId);
};

/*
 * Get the current user attributes along with the group attribute
 */
export const currentUserAttributes = async () => {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const payload = session.tokens.idToken.payload;
    const groups = prop('cognito:groups', payload);

    // get groupnames that start with the account id prefix and not SSO groups
    const accountIdsWithSubgroups = filter(a => a.startsWith('A1'), groups);

    // split on '#', we currently only care about the account id part of the group A1xxx#sub-group to get the venue list and get map notes for account
    // the subgroup currently only matters to the backend team
    const accountIds = map(a => a.split('#')[0], accountIdsWithSubgroups);

    const attributes = await fetchUserAttributes();

    // Get the first account id, not dealing with multiple groups at this time
    return assoc('custom:AccountId', accountIds[0], attributes);
};

export const accessToken = async () => {
    try {
        // Get the current session jwtToken
        const session = await fetchAuthSession();

        if (session.tokens) return path(['tokens', 'accessToken', 'toString'], session)();

        // Need to manually rehydrate the session since Auth.currentSession() doesn't get a refreshed id token
        const refreshedSession = await fetchAuthSession({ forceRefresh: true });
        return path(['tokens', 'accessToken', 'toString'], refreshedSession)();
    } catch (e) {
        console.log('Need to refresh token', e);
        const session = await fetchAuthSession({ forceRefresh: true });
        return path(['tokens', 'accessToken', 'toString'], session)();
    }
};

/*
 * Authenticate email/password against stored credentials in Cognito and return the user's data on success
 * Impure: calls Cognito
 * @sig authenticateUser :: (String, String) -> Promise Object
 */

// This is where users are logged in
export const authenticateUser = async (email, password) => {
    email = email.toLowerCase();
    try {
        const { isSignedIn, nextStep } = await signIn({ username: email, password });

        if (nextStep.signInStep === CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED) {
            // redirect to new password dialog
            return { code: NEW_PASSWORD_REQUIRED };
        }

        if (nextStep.signInStep === DONE) {
            const data = await currentUserAttributes();
            return { loggedIn: true, userData: data };
        }
        console.log({ isSignedIn, nextStep });
        throw new Error('Error with login');
    } catch (err) {
        console.log('Error during authentication', err);
        if (err.name === 'UserAlreadyAuthenticatedException') {
            const data = await currentUserAttributes();
            return { loggedIn: true, userData: data };
        }
        return authFailureObject(err);
    }
};

/*
 * Complete user signup by providing minimum data necessary; returns
 * Impure: calls Cognito
 * @sig completeSignup :: ({ email: String, password: String, newPassword: String, firstName: String, lastName: String }) -> Promise Object
 */
export const completeSignup = async ({ email, password, newPassword, firstName, lastName }) => {
    email = email.toLowerCase();
    try {
        let { nextStep } = await signIn({ username: email, password });

        if (nextStep.signInStep === CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED) {
            const { isSignedIn, nextStep } = await confirmSignIn({
                challengeResponse: newPassword,
                options: {
                    userAttributes: {
                        // set these required attributes for the Cognito user pool
                        email,
                        given_name: firstName,
                        family_name: lastName,
                    },
                },
            });
            if (isSignedIn && nextStep.signInStep === DONE) {
                const data = await currentUserAttributes();
                return authSuccessObject(data);
            }
        }

        return authFailureObject({ message: 'Unknown Error' });
    } catch (err) {
        console.error('Error during sign in', err);
        return authFailureObject(err);
    }
};

/*
 * Return user data from Cognito for the current user if possible; otherwise return { loggedIn: false }
 * (User data consists of attributes that were entered for that user into Cognito, including email, account id, etc.)
 * Impure: calls Cognito
 * @sig rehydrateStoredSession :: () -> Promise Object
 */
export const rehydrateStoredSession = async () => {
    try {
        await getCurrentUser();

        const session = await fetchAuthSession();
        if (session.tokens) {
            const data = await currentUserAttributes();
            return { loggedIn: true, userData: data };
        }
        return { loggedIn: false };
    } catch (e) {
        console.log('Failed to rehydrate session', e);
        return { loggedIn: false };
    }
};

/*
 * Changes user password
 * @sig changePassword :: () -> Promise Object
 */
export const changePassword = async (oldPassword, newPassword) => {
    try {
        const user = await getCurrentUser();
        if (user) {
            await updatePassword({ oldPassword, newPassword });
            return 'SUCCESS';
        }

        throw new Error('Error changing password');
    } catch (e) {
        console.log(e);
        return e;
    }
};

/*
 * Sends an email with a code to reset password
 * @sig forgotPasswordEmail :: String -> Promise Object
 */
export const forgotPasswordEmail = async email => {
    try {
        email = email.toLowerCase();
        const result = await resetPassword({ username: email });
        return result;
    } catch (e) {
        console.error(e);
        return e;
    }
};

/*
 * Changes user password if the code is correct
 * @sig forgotPasswordSubmit :: (String, String, String) -> Promise void
 */
export const forgotPasswordSubmit = async (email, code, newPassword) => {
    try {
        email = email.toLowerCase();
        await confirmResetPassword({ username: email, newPassword, confirmationCode: code });
    } catch (e) {
        console.error(e);
        throw e;
    }
};

/*
 * Update any or all of the users email, first name, or last name
 * If email is being updated, a verification code will be sent
 * @sig updateAttributes :: (String, String, String) -> Promise Object
 */
export const updateAttributes = async (email, firstName, lastName) => {
    email = email.toLowerCase();

    const updateAttr = {};

    if (email) updateAttr.email = email;
    if (firstName) updateAttr.given_name = firstName;
    if (lastName) updateAttr.family_name = lastName;
    try {
        await updateUserAttributes({ userAttributes: updateAttr });
        return 'SUCCESS';
    } catch (e) {
        console.error(e);
        return e;
    }
};

/*
 * Verify the email attribute is being changed for the current user
 * @sig verifyEmailAttr :: String -> Promise void
 */
export const verifyEmailAttr = async code => {
    try {
        await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
    } catch (e) {
        console.error(e);
        throw e;
    }
};
